import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useSelector } from 'react-redux';

import { MEDIA_TYPE, TRANSFORMATION_TYPES } from 'constants/MediaConstants';
import type { AbstractMedia } from 'modules/medias/types/abstract-media';
import type { ImageMedia } from 'modules/medias/types/image-media';

import { getCurrentUserIsReadOnly } from 'modules/users/selectors/currentUser';

import type { FBItemType } from 'ui/base/Dropdown';

import type { MediaGalleryActions, MediaListItem } from '../../types';

type UseActionsMenuItemsParams = {
  showClipping?: boolean;
  media: MediaListItem<ImageMedia | AbstractMedia>;
} & Pick<MediaGalleryActions, 'onDownload' | 'onRetry' | 'onDelete'>;

type UseActionsMenuItemsResult = {
  items: FBItemType[];
  hasVisibleItems: boolean;
};

type UseActionsMenuItems = (params: UseActionsMenuItemsParams) => UseActionsMenuItemsResult;

const useActionsMenuItems: UseActionsMenuItems = ({ onDownload, onRetry, onDelete, media, showClipping = false }) => {
  const isReadOnly = useSelector(getCurrentUserIsReadOnly);
  const { data } = media;
  const isArchived = data.archived;
  const disabled = isArchived || isReadOnly;

  const isImage = data.type === MEDIA_TYPE.IMAGE;
  const isClipped = 'transformations' in data && data.transformations?.includes(TRANSFORMATION_TYPES.CLIPPING);
  const canDisplayClipping = showClipping && isImage && !isClipped;
  const isPendingFile = Boolean(!(data?.thumbnailPath || data?.path) && data?.deviceId);

  const items: FBItemType[] = [
    {
      key: 'clipping',
      'data-cy': 'clip',
      hide: disabled || !canDisplayClipping || isPendingFile,
      icon: <i className="icon fb-icon--layers" />,
      label: <FormattedMessage id="clipping.clip" defaultMessage="Clip" />
    },
    {
      key: 'download',
      icon: <span className="fb-icon--download" />,
      'data-cy': 'media-actions-download',
      disabled: isArchived,
      hide: typeof onDownload !== 'function' || isPendingFile,
      label: <FormattedMessage id="download" defaultMessage="Download" />
    },
    {
      key: 'retry',
      icon: <span className="fb-icon--refresh" />,
      'data-cy': 'media-actions-retry',
      hide: disabled || typeof onRetry !== 'function' || isPendingFile,
      label: <FormattedMessage id="retryUpload" defaultMessage="Retry upload" />
    },
    {
      type: 'divider' as const,
      hide: disabled || typeof onDelete !== 'function' || isPendingFile
    },
    {
      danger: true,
      key: 'delete',
      hide: disabled || typeof onDelete !== 'function',
      'data-cy': 'media-actions-delete',
      icon: <span className="fb-icon fb-icon--delete" />,
      label: <FormattedMessage id="delete" defaultMessage="Delete" />
    }
  ];

  return {
    items,
    hasVisibleItems: items.some(item => !item.hide && item.type !== 'divider'),
  };
};

export default useActionsMenuItems;
